/**
 * Iter 45 · Public onboarding form for prospects.
 *
 * Route: /onboarding/:token   (public, no auth required, no admin layout)
 *
 * Fetches the token from `/api/public/onboarding/:token`:
 *   - 200: renders the filtered general_data_fields for the prospect's industry.
 *   - 404 / 410: shows a friendly "link no longer valid" screen.
 *
 * On submit → POST `/api/public/onboarding/:token/submit`. Token becomes
 * single-use; a second submission is rejected with 410.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIOLET = '#7C6BEF';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/bwe3ss1d_1.png';

export default function PublicOnboarding() {
    const { token } = useParams();
    const [state, setState] = useState('loading'); // loading | ready | invalid | submitted | submitting
    const [error, setError] = useState('');
    const [payload, setPayload] = useState(null); // {prospect, industry, industry_matched, general_data_fields}
    const [responses, setResponses] = useState({});

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/public/onboarding/${token}`).then((r) => {
            if (!alive) return;
            setPayload(r.data);
            setState('ready');
        }).catch((err) => {
            if (!alive) return;
            const detail = err?.response?.data?.detail || '';
            const status = err?.response?.status;
            if (status === 404 || status === 410) {
                setError(detail || 'Este enlace ya no es válido');
            } else {
                setError('No pudimos cargar el formulario. Intenta de nuevo más tarde.');
            }
            setState('invalid');
        });
        return () => { alive = false; };
    }, [token]);

    const setValue = (key, value) => setResponses((p) => ({ ...p, [key]: value }));

    const requiredMissing = useMemo(() => {
        if (!payload) return [];
        return (payload.general_data_fields || [])
            .filter((f) => f.required)
            .filter((f) => {
                const v = responses[f.key];
                return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
            })
            .map((f) => f.label);
    }, [payload, responses]);

    const submit = async () => {
        if (requiredMissing.length) {
            toast.error(`Faltan campos: ${requiredMissing.slice(0, 3).join(', ')}${requiredMissing.length > 3 ? '…' : ''}`);
            return;
        }
        setState('submitting');
        try {
            await axios.post(`${API}/public/onboarding/${token}/submit`, { responses });
            setState('submitted');
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Error al enviar los datos';
            const status = err?.response?.status;
            if (status === 410) {
                setError(detail || 'Este enlace ya fue utilizado');
                setState('invalid');
            } else {
                toast.error(detail);
                setState('ready');
            }
        }
    };

    return (
        <div className="min-h-screen" style={{ background: '#F0F0F3' }}>
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <img src={LOGO_URL} alt="AIGIS Certificate" style={{ height: 36 }} />
                    <Link to="/" className="text-xs uppercase tracking-widest font-bold text-gray-500 hover:text-black">
                        ← aigiscertificate.com
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                {state === 'loading' && (
                    <div className="p-16 bg-white border text-center flex flex-col items-center gap-3 text-gray-500">
                        <Loader2 className="w-8 h-8 animate-spin" />
                        Cargando formulario…
                    </div>
                )}

                {state === 'invalid' && (
                    <div className="p-10 bg-white border-2 border-red-500" data-testid="onboarding-invalid-screen">
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                            <h1 className="font-display font-black text-2xl">Este enlace ya no es válido</h1>
                        </div>
                        <p className="text-sm text-gray-700 mb-4">
                            {error || 'El enlace expiró o ya fue utilizado.'} Si necesitas completar tus datos,
                            contacta al equipo de AIGIS Certificate para recibir un nuevo enlace.
                        </p>
                        <a href="mailto:notificaciones@aigiscertificate.com"
                            className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: VIOLET }}>
                            Escribir a notificaciones@aigiscertificate.com
                        </a>
                    </div>
                )}

                {state === 'submitted' && (
                    <div className="p-10 bg-white border-2 border-emerald-600" data-testid="onboarding-submitted-screen">
                        <div className="flex items-center gap-3 mb-3">
                            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                            <h1 className="font-display font-black text-2xl">¡Gracias!</h1>
                        </div>
                        <p className="text-sm text-gray-700">
                            Recibimos tus datos correctamente. Nuestro equipo los revisará y se pondrá en contacto
                            contigo en las próximas 24 horas hábiles para avanzar con tu distintivo.
                        </p>
                    </div>
                )}

                {(state === 'ready' || state === 'submitting') && payload && (
                    <>
                        <div className="bg-white border p-6 mb-6">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>
                                Formulario de onboarding
                            </div>
                            <h1 className="font-display font-black text-2xl mt-1">
                                Hola{payload.prospect?.name ? `, ${payload.prospect.name}` : ''} 👋
                            </h1>
                            <p className="text-sm text-gray-600 mt-2">
                                Completa los siguientes campos para que podamos preparar tu distintivo.
                            </p>
                            {payload.industry_matched ? (
                                <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold px-3 py-1"
                                    style={{ background: 'rgba(124,107,239,0.1)', color: VIOLET }}>
                                    <ShieldCheck className="w-3.5 h-3.5" />
                                    Adaptado a tu giro: {payload.industry}
                                </div>
                            ) : (
                                <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold px-3 py-1"
                                    style={{ background: 'rgba(245,158,11,0.1)', color: '#B45309' }}>
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    Sin giro específico — te mostramos todos los campos
                                </div>
                            )}
                        </div>

                        <form onSubmit={(e) => { e.preventDefault(); submit(); }}
                            className="bg-white border p-6 space-y-4"
                            data-testid="onboarding-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(payload.general_data_fields || []).map((field) => (
                                    <FieldControl
                                        key={field.key}
                                        field={field}
                                        value={responses[field.key]}
                                        onChange={(v) => setValue(field.key, v)}
                                    />
                                ))}
                            </div>
                            <div className="pt-4 border-t flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-xs text-gray-500">
                                    Tus datos se envían de forma segura y solo el equipo de AIGIS podrá verlos.
                                </div>
                                <button type="submit" disabled={state === 'submitting'}
                                    data-testid="onboarding-submit-btn"
                                    className="inline-flex items-center gap-2 px-6 py-2.5 text-white font-bold text-sm disabled:opacity-50"
                                    style={{ background: VIOLET }}>
                                    {state === 'submitting' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Enviar datos
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </main>
        </div>
    );
}

function FieldControl({ field, value, onChange }) {
    const label = field.label || field.key;
    const type = field.type || 'text';
    const placeholder = field.placeholder || '';
    const required = !!field.required;

    if (type === 'yesno') {
        // Store the value as "Sí"/"No" string to match the format used by
        // InspectionForm's GeneralField — keeps onboarding_data and the
        // auditor's general_data on the same shape so downstream comparisons
        // and the "Dar de alta" pre-fill work uniformly.
        return (
            <div>
                <Label label={label} required={required} />
                <div className="inline-flex border" style={{ borderColor: 'var(--border)' }}>
                    {['Sí', 'No'].map((opt) => {
                        const active = value === opt;
                        return (
                            <button type="button" key={opt}
                                onClick={() => onChange(opt)}
                                data-testid={`field-${field.key}-${opt === 'Sí' ? 'si' : 'no'}`}
                                className="px-5 py-2 text-sm font-bold transition-colors"
                                style={active
                                    ? { background: '#111', color: '#fff' }
                                    : { background: 'white', color: '#111' }}>
                                {opt}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (type === 'textarea') {
        return (
            <div className="md:col-span-2">
                <Label label={label} required={required} />
                <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`field-${field.key}`}
                    className="w-full px-3 py-2 border border-gray-300 outline-none focus:border-black text-sm" />
            </div>
        );
    }
    if (type === 'select' && Array.isArray(field.options)) {
        return (
            <div>
                <Label label={label} required={required} />
                <select value={value || ''} onChange={(e) => onChange(e.target.value)}
                    data-testid={`field-${field.key}`}
                    className="w-full px-3 py-2 border border-gray-300 outline-none focus:border-black text-sm bg-white">
                    <option value="">— Seleccionar —</option>
                    {field.options.map((op) => (
                        <option key={typeof op === 'string' ? op : op.value} value={typeof op === 'string' ? op : op.value}>
                            {typeof op === 'string' ? op : op.label}
                        </option>
                    ))}
                </select>
            </div>
        );
    }
    if (type === 'number') {
        // Store as a real number (or empty string when cleared) so admin
        // consumers can compare numerically instead of parsing again.
        const asNumber = (v) => (v === '' ? '' : Number(v));
        return (
            <div>
                <Label label={label} required={required} />
                <input type="number" inputMode="decimal" step="any"
                    value={value ?? ''}
                    onChange={(e) => onChange(asNumber(e.target.value))}
                    placeholder={placeholder}
                    data-testid={`field-${field.key}`}
                    className="w-full px-3 py-2 border border-gray-300 outline-none focus:border-black text-sm" />
            </div>
        );
    }
    // default: text / email / phone
    return (
        <div>
            <Label label={label} required={required} />
            <input type={type === 'email' ? 'email' : 'text'}
                value={value || ''} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={`field-${field.key}`}
                className="w-full px-3 py-2 border border-gray-300 outline-none focus:border-black text-sm" />
        </div>
    );
}

function Label({ label, required }) {
    return (
        <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
            {label} {required && <span className="text-red-600">*</span>}
        </label>
    );
}
